import React, { useState } from 'react';
import {
  Form,
  Select,
  InputNumber,
  Upload,
  Button,
  Card,
  Steps,
  Table,
  Tag,
  Alert,
  Space,
  Divider,
  Statistic,
  Row,
  Col,
  AutoComplete,
  Checkbox,
  Tabs,
} from 'antd';
import {
  FileExcelOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { API } from '@/services/api.service';
import { useNotify } from '@/hooks';
import AopFindingsAlert from './AopFindingsAlert';

const { Option } = Select;
const { Dragger } = Upload;

export default function UploadPanel({
  companies,
  companySizes,
  onSuccess,
  getLocalizedLabel,
}) {
  const { t } = useTranslation(['tables', 'forms', 'messages', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });
  const notification = useNotify();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentYearFile, setCurrentYearFile] = useState(null);
  const [previousYearFiles, setPreviousYearFiles] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedBilansStanja, setEditedBilansStanja] = useState([]);
  const [editedBilansUspeha, setEditedBilansUspeha] = useState([]);
  const [analyticalFile, setAnalyticalFile] = useState(null);
  const [generateStatisticki, setGenerateStatisticki] = useState(false);
  const [editedStatisticki, setEditedStatisticki] = useState([]);

  // Upload and preview mutation
  const previewMutation = useMutation({
    mutationFn: async (formData) => {
      return await API.financialStatements.uploadAndPreview(formData);
    },
    onSuccess: (response) => {
      setPreviewData(response.data);
      // Initialize editable copies
      setEditedBilansStanja(response.data.bilansStanja || []);
      setEditedBilansUspeha(response.data.bilansUspeha || []);
      setEditedStatisticki(response.data.statistickiIzvestaj || []);
      setCurrentStep(1);
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || fs('uploadError'),
      });
    },
  });

  // Create statement mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await API.financialStatements.createOrUpdateStatement(data);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message || t('createSuccess', { ns: 'messages' }),
      });
      onSuccess();
      resetForm();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || fs('createError'),
      });
    },
  });

  const resetForm = () => {
    form.resetFields();
    setCurrentYearFile(null);
    setPreviousYearFiles([]);
    setAnalyticalFile(null);
    setGenerateStatisticki(false);
    setPreviewData(null);
    setEditedBilansStanja([]);
    setEditedBilansUspeha([]);
    setEditedStatisticki([]);
    setCurrentStep(0);
  };

  // Recalculate summary rows based on summary definitions from backend
  const recalculateSummaries = (rows, summaryDefs, field) => {
    if (!summaryDefs) return rows;
    const map = new Map(rows.map((r) => [r.aopCode, { ...r }]));

    // Sort summary keys descending (children before parents)
    const keys = Object.keys(summaryDefs).sort((a, b) => parseInt(b) - parseInt(a));

    // Multiple passes to resolve nested summaries
    for (let pass = 0; pass < 3; pass++) {
      for (const parentCode of keys) {
        const childCodes = summaryDefs[parentCode];
        let sum = 0;
        let hasAny = false;
        for (const code of childCodes) {
          const isNegative = code.startsWith('-');
          const actualCode = isNegative ? code.slice(1) : code;
          const child = map.get(actualCode);
          if (child && child[field] != null) {
            sum += isNegative ? -child[field] : child[field];
            hasAny = true;
          }
        }
        const parent = map.get(parentCode);
        if (parent) {
          parent[field] = hasAny ? Math.round(sum) : null;
          map.set(parentCode, parent);
        }
      }
    }
    return rows.map((r) => map.get(r.aopCode) || r);
  };

  // Handle cell edit for Bilans stanja
  const handleBilansStanjaEdit = (aopCode, field, value) => {
    setEditedBilansStanja((prev) => {
      const updated = prev.map((item) =>
        item.aopCode === aopCode ? { ...item, [field]: value } : item
      );
      return recalculateSummaries(updated, previewData?.summaryDefinitions?.bilansStanja, field);
    });
  };

  // Handle cell edit for Bilans uspeha
  const handleBilansUspehaEdit = (aopCode, field, value) => {
    setEditedBilansUspeha((prev) => {
      const updated = prev.map((item) =>
        item.aopCode === aopCode ? { ...item, [field]: value } : item
      );
      return recalculateSummaries(updated, previewData?.summaryDefinitions?.bilansUspeha, field);
    });
  };

  // Handle cell edit for Statistički izveštaj
  const handleStatistickiEdit = (aopCode, field, value) => {
    setEditedStatisticki((prev) =>
      prev.map((item) =>
        item.aopCode === aopCode ? { ...item, [field]: value, isManualOverride: true } : item
      )
    );
  };

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();

      if (!currentYearFile) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: fs('currentYearFileRequired'),
        });
        return;
      }

      const formData = new FormData();
      formData.append('currentYearFile', currentYearFile);
      formData.append('year', values.year);
      formData.append('companySize', values.companySize);

      // Add previous year XML files if provided
      for (const xmlFile of previousYearFiles) {
        formData.append('previousYearFile', xmlFile);
      }

      // Add analytical file and SI options
      if (analyticalFile && generateStatisticki) {
        formData.append('analyticalFile', analyticalFile);
        formData.append('generateStatisticki', 'true');
        // Build manual data from form
        const manualData = {
          monthsOperating: values.monthsOperating ?? 12,
          ownershipType: values.ownershipType ?? 2,
          foreignEntitiesCount: values.foreignEntitiesCount ?? 0,
          foreignEntities10PctCount: values.foreignEntities10PctCount ?? 0,
          averageEmployees: values.averageEmployees,
          agencyEmployees: values.agencyEmployees ?? 0,
          volunteers: values.volunteers ?? 0,
        };
        formData.append('statistickiManualData', JSON.stringify(manualData));
      }

      // Company can be selected from dropdown or entered manually
      if (values.companyName) {
        // Check if it matches a company from dropdown
        const matchedCompany = companies.find(c => c.companyName === values.companyName);
        if (matchedCompany) {
          formData.append('companyId', matchedCompany._id);
        }
        formData.append('companyName', values.companyName);
      }

      previewMutation.mutate(formData);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleConfirm = () => {
    if (!previewData) return;

    setIsSubmitting(true);

    const data = {
      year: previewData.year,
      companySize: previewData.companySize,
      accountBalances: previewData.accountBalances,
      bilansStanja: editedBilansStanja,  // Use edited values
      bilansUspeha: editedBilansUspeha,  // Use edited values
      previousYearSource: previewData.previousYearSource,
    };

    // Add SI data if generated
    if (previewData.generateStatisticki && editedStatisticki.length > 0) {
      data.statistickiIzvestaj = editedStatisticki;
      data.statistickiData = previewData.statistickiData || null;
      data.analyticalAccountBalances = previewData.analyticalAccountBalances || [];
    }

    // Add company info if available
    if (previewData.company?._id) {
      data.companyId = previewData.company._id;
    }
    if (previewData.companyName) {
      data.companyName = previewData.companyName;
    }

    createMutation.mutate(data, {
      onSettled: () => setIsSubmitting(false),
    });
  };

  // Check if file is Excel
  const isExcelFile = (f) => {
    return f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      || f.type === 'application/vnd.ms-excel'
      || f.name.endsWith('.xlsx')
      || f.name.endsWith('.xls');
  };

  // Check if file is XML
  const isXmlFile = (f) => {
    return f.type === 'application/xml'
      || f.type === 'text/xml'
      || f.name.toLowerCase().endsWith('.xml');
  };

  // Current year file upload props
  const currentYearUploadProps = {
    beforeUpload: (f) => {
      if (!isExcelFile(f)) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: fs('onlyExcelFiles'),
        });
        return false;
      }
      setCurrentYearFile(f);
      return false;
    },
    onRemove: () => {
      setCurrentYearFile(null);
    },
    fileList: currentYearFile ? [currentYearFile] : [],
    maxCount: 1,
    accept: '.xlsx,.xls',
  };

  // Previous year XML upload props
  const previousYearUploadProps = {
    beforeUpload: (f) => {
      if (!isXmlFile(f)) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: fs('onlyXmlFiles'),
        });
        return false;
      }
      if (previousYearFiles.length >= 3) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: fs('xmlPreviousYearMax'),
        });
        return false;
      }
      setPreviousYearFiles((prev) => [...prev, f]);
      return false;
    },
    onRemove: (f) => {
      setPreviousYearFiles((prev) => prev.filter((item) => item.uid !== f.uid));
    },
    fileList: previousYearFiles,
    maxCount: 3,
    multiple: true,
    accept: '.xml',
  };

  // Analytical file upload props
  const analyticalUploadProps = {
    beforeUpload: (f) => {
      if (!isExcelFile(f)) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: fs('onlyExcelFiles'),
        });
        return false;
      }
      setAnalyticalFile(f);
      setGenerateStatisticki(true);
      return false;
    },
    onRemove: () => {
      setAnalyticalFile(null);
      setGenerateStatisticki(false);
    },
    fileList: analyticalFile ? [analyticalFile] : [],
    maxCount: 1,
    accept: '.xlsx,.xls',
  };

  // SI editable columns — handles both SINGLE and BRUTO_ISPRAVKA_NETO AOP codes
  const isBrutoIspravkaAop = (aopCode) => {
    const code = parseInt(aopCode);
    return (code >= 9008 && code <= 9037) || (code >= 9127 && code <= 9136);
  };

  const statistickiEditableColumns = [
    {
      title: 'AOP',
      dataIndex: 'aopCode',
      key: 'aopCode',
      width: 70,
      fixed: 'left',
    },
    {
      title: fs('currentYear'),
      key: 'col1',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const isBIN = isBrutoIspravkaAop(record.aopCode);
        const field = isBIN ? 'bruto' : 'currentYear';
        const val = record[field];
        return (
          <InputNumber
            size="small"
            value={val}
            onChange={(newVal) => handleStatistickiEdit(record.aopCode, field, newVal)}
            style={{ width: '100%' }}
            formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
            parser={(v) => v.replace(/[^\d-]/g, '')}
          />
        );
      },
    },
    {
      title: fs('previousYear'),
      key: 'col2',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const isBIN = isBrutoIspravkaAop(record.aopCode);
        const field = isBIN ? 'ispravka' : 'previousYear';
        const val = record[field];
        return (
          <InputNumber
            size="small"
            value={val}
            onChange={(newVal) => handleStatistickiEdit(record.aopCode, field, newVal)}
            style={{ width: '100%' }}
            formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
            parser={(v) => v.replace(/[^\d-]/g, '')}
          />
        );
      },
    },
    {
      title: fs('neto'),
      key: 'col3',
      width: 100,
      align: 'right',
      render: (_, record) => {
        if (!isBrutoIspravkaAop(record.aopCode)) return '-';
        return record.neto !== null && record.neto !== undefined ? record.neto.toLocaleString() : '-';
      },
    },
  ];

  // Editable columns for Bilans stanja (summary rows are bold and have light background)
  const bilansStanjaEditableColumns = [
    {
      title: 'AOP',
      dataIndex: 'aopCode',
      key: 'aopCode',
      width: 80,
      render: (val, record) => (
        <span style={{ fontWeight: record.isSummary ? 'bold' : 'normal' }}>
          {val}
          {record.isSummary && <Tag size="small" color="blue" style={{ marginLeft: 4, fontSize: 10 }}>Σ</Tag>}
        </span>
      ),
    },
    {
      title: fs('currentYear'),
      dataIndex: 'currentYear',
      key: 'currentYear',
      width: 130,
      align: 'right',
      render: (val, record) => (
        <InputNumber
          size="small"
          value={val}
          onChange={(newVal) => handleBilansStanjaEdit(record.aopCode, 'currentYear', newVal)}
          style={{ width: '100%', fontWeight: record.isSummary ? 'bold' : 'normal' }}
          formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
          parser={(v) => v.replace(/[^\d-]/g, '')}
        />
      ),
    },
    {
      title: fs('previousYear'),
      dataIndex: 'previousYear',
      key: 'previousYear',
      width: 130,
      align: 'right',
      render: (val, record) => (
        <InputNumber
          size="small"
          value={val}
          onChange={(newVal) => handleBilansStanjaEdit(record.aopCode, 'previousYear', newVal)}
          style={{ width: '100%', fontWeight: record.isSummary ? 'bold' : 'normal' }}
          formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
          parser={(v) => v.replace(/[^\d-]/g, '')}
        />
      ),
    },
  ];

  // Editable columns for Bilans uspeha (summary rows are bold and have Σ tag)
  const bilansUspehaEditableColumns = [
    {
      title: 'AOP',
      dataIndex: 'aopCode',
      key: 'aopCode',
      width: 80,
      render: (val, record) => (
        <span style={{ fontWeight: record.isSummary ? 'bold' : 'normal' }}>
          {val}
          {record.isSummary && <Tag size="small" color="green" style={{ marginLeft: 4, fontSize: 10 }}>Σ</Tag>}
        </span>
      ),
    },
    {
      title: fs('currentYear'),
      dataIndex: 'currentYear',
      key: 'currentYear',
      width: 130,
      align: 'right',
      render: (val, record) => (
        <InputNumber
          size="small"
          value={val}
          onChange={(newVal) => handleBilansUspehaEdit(record.aopCode, 'currentYear', newVal)}
          style={{ width: '100%', fontWeight: record.isSummary ? 'bold' : 'normal' }}
          formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
          parser={(v) => v.replace(/[^\d-]/g, '')}
        />
      ),
    },
    {
      title: fs('previousYear'),
      dataIndex: 'previousYear',
      key: 'previousYear',
      width: 130,
      align: 'right',
      render: (val, record) => (
        <InputNumber
          size="small"
          value={val}
          onChange={(newVal) => handleBilansUspehaEdit(record.aopCode, 'previousYear', newVal)}
          style={{ width: '100%', fontWeight: record.isSummary ? 'bold' : 'normal' }}
          formatter={(v) => v !== null && v !== undefined ? v.toLocaleString() : ''}
          parser={(v) => v.replace(/[^\d-]/g, '')}
        />
      ),
    },
  ];

  // Read-only columns for confirmation step (with summary row highlighting)
  const bilansStanjaColumns = [
    {
      title: 'AOP',
      dataIndex: 'aopCode',
      key: 'aopCode',
      width: 80,
      render: (val, record) => (
        <span style={{ fontWeight: record.isSummary ? 'bold' : 'normal' }}>
          {val}
        </span>
      ),
    },
    {
      title: fs('currentYear'),
      dataIndex: 'currentYear',
      key: 'currentYear',
      width: 120,
      align: 'right',
      render: (val, record) => (
        <span style={{ fontWeight: record.isSummary ? 'bold' : 'normal' }}>
          {val !== null ? val.toLocaleString() : '-'}
        </span>
      ),
    },
    {
      title: fs('previousYear'),
      dataIndex: 'previousYear',
      key: 'previousYear',
      width: 120,
      align: 'right',
      render: (val, record) => (
        <span style={{ fontWeight: record.isSummary ? 'bold' : 'normal' }}>
          {val !== null ? val.toLocaleString() : '-'}
        </span>
      ),
    },
  ];

  const steps = [
    {
      title: fs('stepUpload'),
      description: fs('stepUploadDesc'),
    },
    {
      title: fs('stepPreview'),
      description: fs('stepPreviewDesc'),
    },
    {
      title: fs('stepConfirm'),
      description: fs('stepConfirmDesc'),
    },
  ];

  return (
    <div>
      <Steps current={currentStep} items={steps} className="mb-6" />

      {currentStep === 0 && (
        <Card title={fs('uploadExcel')}>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="companyName"
                  label={fs('company')}
                >
                  <AutoComplete
                    placeholder={fs('enterOrSelectCompany')}
                    options={companies.map((company) => ({
                      value: company.companyName,
                      label: `${company.companyName}${company.taxId ? ` (${company.taxId})` : ''}`,
                      companyId: company._id,
                    }))}
                    filterOption={(inputValue, option) =>
                      option.value.toLowerCase().includes(inputValue.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="year"
                  label={fs('year')}
                  rules={[{ required: true, message: fs('yearRequired') }]}
                  initialValue={new Date().getFullYear() - 1}
                >
                  <InputNumber
                    min={2020}
                    max={2030}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="companySize"
                  label={fs('companySize')}
                  rules={[{ required: true, message: fs('companySizeRequired') }]}
                >
                  <Select placeholder={fs('selectCompanySize')}>
                    {companySizes.map((size) => (
                      <Option key={size.key} value={size.key}>
                        {getLocalizedLabel(size)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      {fs('currentYearFile') || 'Tekuća godina (obavezno)'}
                      <Tag color="red">*</Tag>
                    </Space>
                  }
                  required
                >
                  <Dragger {...currentYearUploadProps}>
                    <p className="ant-upload-drag-icon">
                      <FileExcelOutlined style={{ fontSize: 36, color: '#52c41a' }} />
                    </p>
                    <p className="ant-upload-text">{fs('dragDropExcel') || 'Prevuci bb tekuće godine'}</p>
                    <p className="ant-upload-hint">npr. bb 2025.xlsx</p>
                  </Dragger>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={fs('previousYearFile') || 'Prethodna godina - XML (opciono)'}
                >
                  <Dragger {...previousYearUploadProps}>
                    <p className="ant-upload-drag-icon">
                      <FileTextOutlined style={{ fontSize: 36, color: '#1890ff' }} />
                    </p>
                    <p className="ant-upload-text">{fs('dragDropExcelPrev') || 'Prevuci XML prethodne godine'}</p>
                    <p className="ant-upload-hint">{fs('xmlPreviousYearHint') || '2 fajla: Bilans stanja + Bilans uspeha (.xml)'}</p>
                  </Dragger>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">{fs('statistickiOptional')}</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={fs('analyticalBrutoBilans')}>
                  <Dragger {...analyticalUploadProps}>
                    <p className="ant-upload-drag-icon">
                      <BarChartOutlined style={{ fontSize: 36, color: '#722ed1' }} />
                    </p>
                    <p className="ant-upload-text">{fs('dragDropAnalytical')}</p>
                    <p className="ant-upload-hint">{fs('analyticalHint')}</p>
                  </Dragger>
                </Form.Item>
              </Col>
              <Col span={12}>
                {analyticalFile && (
                  <>
                    <Form.Item>
                      <Checkbox
                        checked={generateStatisticki}
                        onChange={(e) => setGenerateStatisticki(e.target.checked)}
                      >
                        <strong>{fs('generateStatisticki')}</strong>
                      </Checkbox>
                    </Form.Item>
                    {generateStatisticki && (
                      <Card size="small" title={fs('sectionITitle')}>
                        <Row gutter={[8, 0]}>
                          <Col span={12}>
                            <Form.Item name="monthsOperating" label={fs('monthsOperating')} initialValue={12}>
                              <InputNumber min={1} max={12} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="ownershipType" label={fs('ownershipType')} initialValue={2}>
                              <InputNumber min={1} max={9} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="averageEmployees" label={fs('averageEmployees')} rules={[{ required: generateStatisticki, message: fs('required') }]}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="foreignEntitiesCount" label={fs('foreignEntities')} initialValue={0}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="foreignEntities10PctCount" label={fs('foreignEntities10Pct')} initialValue={0}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="agencyEmployees" label={fs('agencyEmployees')} initialValue={0}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    )}
                  </>
                )}
              </Col>
            </Row>


            <div className="flex justify-end">
              <Button
                type="primary"
                className="btn-primary"
                icon={<ArrowRightOutlined />}
                onClick={handleUpload}
                loading={previewMutation.isPending}
                disabled={!currentYearFile}
              >
                {fs('uploadAndPreview')}
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {currentStep === 1 && previewData && (
        <div>
          <Card title={fs('previewTitle')} className="mb-4">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title={fs('company')}
                  value={previewData.companyName || previewData.company?.companyName || '-'}
                />
              </Col>
              <Col span={6}>
                <Statistic title={fs('year')} value={previewData.year} formatter={(val) => val} />
              </Col>
              <Col span={6}>
                <Statistic
                  title={fs('companySize')}
                  value={getLocalizedLabel(companySizes.find((s) => s.key === previewData.companySize) || {})}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={fs('accountsCount')}
                  value={previewData.accountBalances?.length || 0}
                />
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={12}>
                <Space>
                  <strong>{fs('previousYearSource') || 'Izvor prethodne godine'}:</strong>
                  {previewData.previousYearSource === 'FROM_XML' && (
                    <Tag color="green">XML fajlovi</Tag>
                  )}
                  {previewData.previousYearSource === 'FROM_EXCEL' && (
                    <Tag color="green">Excel fajl</Tag>
                  )}
                  {previewData.previousYearSource === 'FROM_DATABASE' && (
                    <Tag color="blue">Baza podataka</Tag>
                  )}
                  {!previewData.previousYearSource && (
                    <Tag color="orange">Nije dostupno</Tag>
                  )}
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <strong>{fs('hasPreviousYear') || 'Kolona 6 (prethodna godina)'}:</strong>
                  {previewData.hasPreviousYear ? (
                    <Tag color="green">✓ Popunjeno</Tag>
                  ) : (
                    <Tag color="red">✗ Prazno</Tag>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {previewData.validation && !previewData.validation.isValid && (
            <Alert
              type="error"
              message={fs('validationErrors')}
              description={
                <ul>
                  {previewData.validation.errors.map((err, idx) => (
                    <li key={idx}>{err.message}</li>
                  ))}
                </ul>
              }
              className="mb-4"
            />
          )}

          <AopFindingsAlert
            warnings={previewData.validation?.warnings}
            className="mb-4"
          />

          <Alert
            type="info"
            message={fs('editableHint') || 'Možete ručno izmeniti vrednosti u tabelama pre potvrde.'}
            className="mb-4"
            showIcon
          />

          <Tabs
            defaultActiveKey="bsbu"
            items={[
              {
                key: 'bsbu',
                label: (
                  <Space>
                    Bilans Stanja / Uspeha
                    <Tag color="blue">{(editedBilansStanja.length || 0) + (editedBilansUspeha.length || 0)} AOP</Tag>
                  </Space>
                ),
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card
                        title={
                          <Space>
                            {fs('bilansStanja')}
                            <Tag color={previewData.mappingsAvailable?.bilansStanja ? 'green' : 'red'}>
                              {editedBilansStanja.length || 0} AOP
                            </Tag>
                          </Space>
                        }
                        size="small"
                      >
                        <Table
                          dataSource={editedBilansStanja}
                          columns={bilansStanjaEditableColumns}
                          rowKey="aopCode"
                          size="small"
                          pagination={false}
                          scroll={{ y: 400 }}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card
                        title={
                          <Space>
                            {fs('bilansUspeha')}
                            <Tag color={previewData.mappingsAvailable?.bilansUspeha ? 'green' : 'red'}>
                              {editedBilansUspeha.length || 0} AOP
                            </Tag>
                          </Space>
                        }
                        size="small"
                      >
                        <Table
                          dataSource={editedBilansUspeha}
                          columns={bilansUspehaEditableColumns}
                          rowKey="aopCode"
                          size="small"
                          pagination={false}
                          scroll={{ y: 400 }}
                        />
                      </Card>
                    </Col>
                  </Row>
                ),
              },
              ...(previewData.generateStatisticki ? [{
                key: 'si',
                label: (
                  <Space>
                    <BarChartOutlined />
                    {fs('statistickiIzvestaj')}
                    <Tag color="purple">{editedStatisticki.filter(s => s.currentYear !== null || s.bruto !== null).length} AOP</Tag>
                  </Space>
                ),
                children: (
                  <Card
                    title={
                      <Space>
                        {fs('statistickiIzvestaj')}
                        <Tag color={previewData.mappingsAvailable?.statistickiIzvestaj ? 'green' : 'orange'}>
                          {editedStatisticki.length || 0} {fs('aopTotal')}
                        </Tag>
                      </Space>
                    }
                    size="small"
                  >
                    <Table
                      dataSource={editedStatisticki.filter(s =>
                        s.currentYear !== null || s.previousYear !== null ||
                        s.bruto !== null || s.ispravka !== null || s.neto !== null
                      )}
                      columns={statistickiEditableColumns}
                      rowKey="aopCode"
                      size="small"
                      pagination={false}
                      scroll={{ y: 500 }}
                    />
                  </Card>
                ),
              }] : []),
            ]}
          />

          <Divider />

          <div className="flex justify-between">
            <Button
              type="default"
              className="btn-default btn-default--hover"
              icon={<ArrowLeftOutlined />}
              onClick={() => setCurrentStep(0)}
            >
              {t('back', { ns: 'menus' })}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              icon={<ArrowRightOutlined />}
              onClick={() => setCurrentStep(2)}
              disabled={!previewData.validation?.isValid}
            >
              {t('next', { ns: 'menus' })}
            </Button>
          </div>
        </div>
      )}

      {currentStep === 2 && previewData && (
        <div>
          <Card title={fs('confirmGeneration')} className="mb-4">
            <Alert
              type="info"
              message={fs('confirmMessage')}
              description={fs('confirmDescription')}
              showIcon
              className="mb-4"
            />

            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Statistic
                  title={fs('company')}
                  value={previewData.companyName || previewData.company?.companyName || '-'}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={fs('year')}
                  value={previewData.year}
                  formatter={(val) => val}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={fs('companySize')}
                  value={previewData.companySize}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={fs('accountsCount')}
                  value={previewData.accountBalances?.length || 0}
                />
              </Col>
            </Row>
          </Card>

          <Row gutter={16} className="mb-4">
            <Col span={previewData.generateStatisticki ? 8 : 12}>
              <Card
                title={
                  <Space>
                    {fs('bilansStanja')}
                    <Tag color="blue">{editedBilansStanja.length || 0} AOP</Tag>
                  </Space>
                }
                size="small"
              >
                <Table
                  dataSource={editedBilansStanja}
                  columns={bilansStanjaColumns}
                  rowKey="aopCode"
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                />
              </Card>
            </Col>
            <Col span={previewData.generateStatisticki ? 8 : 12}>
              <Card
                title={
                  <Space>
                    {fs('bilansUspeha')}
                    <Tag color="green">{editedBilansUspeha.length || 0} AOP</Tag>
                  </Space>
                }
                size="small"
              >
                <Table
                  dataSource={editedBilansUspeha}
                  columns={bilansStanjaColumns}
                  rowKey="aopCode"
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                />
              </Card>
            </Col>
            {previewData.generateStatisticki && editedStatisticki.length > 0 && (
              <Col span={8}>
                <Card
                  title={
                    <Space>
                      <BarChartOutlined />
                      {fs('statistickiIzvestaj')}
                      <Tag color="purple">
                        {editedStatisticki.filter(s => s.currentYear !== null || s.bruto !== null).length} AOP
                      </Tag>
                    </Space>
                  }
                  size="small"
                >
                  <Table
                    dataSource={editedStatisticki.filter(s =>
                      s.currentYear !== null || s.previousYear !== null ||
                      s.bruto !== null || s.ispravka !== null || s.neto !== null
                    )}
                    columns={[
                      { title: 'AOP', dataIndex: 'aopCode', key: 'aopCode', width: 60 },
                      {
                        title: fs('value'),
                        key: 'value',
                        align: 'right',
                        render: (_, r) => {
                          if (r.bruto !== null || r.ispravka !== null || r.neto !== null) {
                            return `${r.bruto ?? '-'} / ${r.ispravka ?? '-'} / ${r.neto ?? '-'}`;
                          }
                          return r.currentYear !== null ? r.currentYear.toLocaleString() : '-';
                        },
                      },
                    ]}
                    rowKey="aopCode"
                    size="small"
                    pagination={false}
                    scroll={{ y: 400 }}
                  />
                </Card>
              </Col>
            )}
          </Row>

          <div className="flex justify-between">
            <Button
              type="default"
              className="btn-default btn-default--hover"
              icon={<ArrowLeftOutlined />}
              onClick={() => setCurrentStep(1)}
            >
              {t('back', { ns: 'menus' })}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              icon={<SaveOutlined />}
              onClick={handleConfirm}
              loading={isSubmitting}
            >
              {fs('generateAndSave')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
